import {useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {Stack} from 'shells/layout/stack/stack';
import {SearchBar} from 'shells/search-bar/search-bar';

import {getFilteredFields, isDisabled} from '../../../editor/preview/add-card/helpers';

import type {NutshellState} from '../../../../../../store';
import {NutshellFieldPicker} from '../../../../../core/nutshell-fields';
import {filterListItems} from '../../../../../core/nutshell-fields/helpers';
import type {FormSchemaProperty} from '../../../../../core/nutshell-fields/types';
import {getFormSchemaFields} from '../../../../forms-selectors';
import type {FormFieldRowForDuplicateHelpers} from '../../../../types';

import type {CustomFieldView, FieldWithArrayKey} from '../blocks-and-design/content-blocks/types';

import {CustomFieldListEmptyState} from './custom-field-list-empty-state';
import {getSearchPlaceholder} from './helpers';

import styles from './existing-field-picker.module.css';

interface Props {
    view: CustomFieldView;
    fields: FieldWithArrayKey[];
    onSelect: (field: FormSchemaProperty) => void;
}

export function ExistingFieldPicker(props: Props) {
    const [searchValue, setSearchValue] = useState('');

    const schemaFields = useSelector((state: NutshellState) => getFormSchemaFields(state));

    const fieldsForDuplicateHelpers = props.fields as FormFieldRowForDuplicateHelpers[];

    const fieldsForCurrentView = useMemo(
        () =>
            getFilteredFields({
                schemaFields,
                fields: fieldsForDuplicateHelpers,
                view: props.view,
            }),
        [schemaFields, fieldsForDuplicateHelpers, props.view]
    );

    return (
        <Stack spacing={8}>
            <div className={styles.search}>
                <SearchBar
                    value={searchValue}
                    onChange={setSearchValue}
                    isClearable={true}
                    hasBorder={true}
                    placeholder={getSearchPlaceholder(props.view)}
                    // @ts-expect-error
                    autofocus={true}
                />
            </div>
            <div className={styles.list}>
                <NutshellFieldPicker
                    onSelectField={props.onSelect}
                    customFilterFunction={(fieldsToFilter: FormSchemaProperty[]) =>
                        getFilteredFields({
                            schemaFields: fieldsToFilter,
                            fields: fieldsForDuplicateHelpers,
                            view: props.view,
                        })
                    }
                    getIsItemDisabled={(field: FormSchemaProperty) =>
                        isDisabled(fieldsForDuplicateHelpers, field)
                    }
                    getDisabledText={() => 'This field is already in your form'}
                    searchValue={searchValue}
                    customSpecialStates={[
                        {
                            shouldRender: () => fieldsForCurrentView.length === 0,
                            component: <CustomFieldListEmptyState view={props.view} />,
                        },
                        {
                            shouldRender: () =>
                                fieldsForCurrentView.length > 0 &&
                                filterListItems(fieldsForCurrentView, searchValue).length === 0,
                            component: (
                                <NothingFoundEmptyState
                                    title='No fields were found'
                                    subtitle='Try loosening your search!'
                                />
                            ),
                        },
                    ]}
                />
            </div>
        </Stack>
    );
}
